import c from "classnames";
import dayjs from "dayjs";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { TableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/TableColumnSettingsProvider";

import { HistoricalActivityTimelineCard } from "@components/Cards/HistoricalActivityTimelineCard/HistoricalActivityTimelineCard";
import { ClientEmployeeAppUsageLineChart } from "@components/Charts";
import {
  DashboardCell,
  DashboardChartFrame,
  DashboardGrid,
  DashboardWidget,
  DashboardWindowBar,
  dashboardRangeEndingToday,
  formatDashboardRangeLabel,
  type DashboardDateRange,
} from "@components/Dashboards/shell";
import {
  EmployeeApplicationFilterForm,
  EmployeeApplicationFilterFormRef,
  EmployeeApplicationFilterFormValues,
} from "@components/Forms";
import {
  EmployeeAppCountUsageStat,
  EmployeeOwnedAppsCountStat,
  EmployeeUsageTimeStat,
} from "@components/Stats";
import { EmployeeApplicationsTableConfig } from "@components/Tables/EmployeeApplicationsTable/EmployeeApplicationsTableConfig";
import { EmployeeApplicationsTableColumnCustomization } from "@components/Tables/EmployeeApplicationsTable/UI/EmployeeApplicationsTableColumnCustomization";
import {
  DEFAULT_EMPLOYEE_APPLICATION_TABLE_COLUMN_ORDER,
  DEFAULT_EMPLOYEE_APPLICATION_TABLE_COLUMN_SETTINGS,
} from "@components/Tables/EmployeeApplicationsTable/UI/EmployeeApplicationsTableColumns";
import { EmployeeResourceGroupsTable } from "@components/Tables/EmployeeResourceGroupsTable/EmployeeResourceGroupsTable";
import { useEmployeeResourceGroupsTableExport } from "@components/Tables/EmployeeResourceGroupsTable/utils/useEmployeeResourceGroupsTableExport";

import { Box } from "@design-system/Box/Box";
import { IconButton } from "@design-system/IconButton/IconButton";
import { SlideInPanel } from "@design-system/SlideInPanel";

import classes from "./EmployeeDashboard.module.css";

type Props = {
  clientEmployeeId: string;
  clientId: string;
  /** Deep-link filter for AF-516 / AF-582: show history for one application. */
  initialApplicationResourceId?: string;
};

const FOLD_HEIGHT = 800;

const scrollParent = (node: HTMLElement): HTMLElement => {
  let current = node.parentElement;
  while (current) {
    const { overflowY } = getComputedStyle(current);
    if (overflowY === "auto" || overflowY === "scroll") {
      return current;
    }
    current = current.parentElement;
  }
  return document.documentElement;
};

const spaceBelow = (node: HTMLElement, scroller: HTMLElement): number => {
  let total = parseFloat(getComputedStyle(node).marginBottom) || 0;
  let current = node.parentElement;
  while (current && current !== scroller) {
    const style = getComputedStyle(current);
    total += parseFloat(style.paddingBottom) || 0;
    total += parseFloat(style.borderBottomWidth) || 0;
    current = current.parentElement;
  }
  return total;
};

export const EmployeeDashboard = ({
  clientEmployeeId,
  clientId,
  initialApplicationResourceId,
}: Props) => {
  const [filterValues, setFilterValues] =
    useState<EmployeeApplicationFilterFormValues>({
      applicationResourceId: initialApplicationResourceId,
      parentCategoryId: undefined,
      createdAt_Gte: undefined,
      createdAt_Lte: undefined,
    });

  useEffect(() => {
    if (!initialApplicationResourceId) {
      return;
    }
    setFilterValues((current) =>
      current.applicationResourceId === initialApplicationResourceId
        ? current
        : {
            ...current,
            applicationResourceId: initialApplicationResourceId,
          },
    );
  }, [initialApplicationResourceId]);

  const filterFormRef = useRef<EmployeeApplicationFilterFormRef>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const [pageHeight, setPageHeight] = useState<number>();
  const [logHeight, setLogHeight] = useState<number>();
  const locked = pageHeight !== undefined;

  const areFiltersActive = useMemo(() => {
    return Boolean(
      (filterValues.applicationResourceId &&
        filterValues.applicationResourceId.trim() !== "") ||
        (filterValues.parentCategoryId &&
          filterValues.parentCategoryId.trim() !== "") ||
        filterValues.createdAt_Gte ||
        filterValues.createdAt_Lte,
    );
  }, [filterValues]);

  const [dateRange, setDateRange] = useState<DashboardDateRange>(() =>
    dashboardRangeEndingToday(30),
  );

  const windowLabel = formatDashboardRangeLabel(dateRange);

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);

  const { exportData, isExporting } = useEmployeeResourceGroupsTableExport({
    clientEmployeeId,
    parentCategoryId: filterValues.parentCategoryId,
    applicationResourceId: filterValues.applicationResourceId,
    dateRange,
  });

  const handleApplyFilters = useCallback(() => {
    if (filterFormRef.current) {
      const currentFormValues = filterFormRef.current.getCurrentValues();
      setFilterValues(currentFormValues);
      setDateRange([
        currentFormValues.createdAt_Gte
          ? dayjs(currentFormValues.createdAt_Gte).toDate()
          : null,
        currentFormValues.createdAt_Lte
          ? dayjs(currentFormValues.createdAt_Lte).toDate()
          : null,
      ]);
    }
  }, []);

  const handleFilterChange = useCallback(
    (newValues: EmployeeApplicationFilterFormValues) => {
      setFilterValues(newValues);
      setIsFilterPanelOpen(false);
      setDateRange([
        newValues.createdAt_Gte ? dayjs(newValues.createdAt_Gte).toDate() : null,
        newValues.createdAt_Lte ? dayjs(newValues.createdAt_Lte).toDate() : null,
      ]);
    },
    [],
  );

  const handleFiltersReset = useCallback(() => {
    setFilterValues({
      applicationResourceId: undefined,
      parentCategoryId: undefined,
      createdAt_Gte: undefined,
      createdAt_Lte: undefined,
    });
    setDateRange(dashboardRangeEndingToday(30));
  }, []);

  useLayoutEffect(() => {
    const node = pageRef.current;
    if (!node) {
      return;
    }

    const measure = () => {
      if (window.innerHeight < FOLD_HEIGHT) {
        setPageHeight(undefined);
        setLogHeight(Math.round(window.innerHeight * 1.5));
        return;
      }

      setLogHeight(undefined);

      const scroller = scrollParent(node);
      const available = Math.floor(
        scroller.getBoundingClientRect().bottom -
          node.getBoundingClientRect().top -
          spaceBelow(node, scroller),
      );
      setPageHeight(Math.max(available, 0));
    };

    measure();
    const scroller = scrollParent(node);
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  const handleExportClick = async () => {
    try {
      await exportData();
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  return (
    <TableColumnSettingsProvider
      tableId={EmployeeApplicationsTableConfig.tableId}
      defaultColumnSettings={DEFAULT_EMPLOYEE_APPLICATION_TABLE_COLUMN_SETTINGS}
      defaultColumnOrder={DEFAULT_EMPLOYEE_APPLICATION_TABLE_COLUMN_ORDER}
    >
      <div
        ref={pageRef}
        className={c(classes.page, locked && classes.locked)}
        style={locked ? { height: pageHeight } : undefined}
      >
      <DashboardGrid>
        <DashboardCell span={4}>
          <EmployeeUsageTimeStat
            prominent
            clientEmployeeId={clientEmployeeId}
            clientId={clientId}
            applicationResourceId={filterValues.applicationResourceId}
          />
        </DashboardCell>
        <DashboardCell span={4}>
          <EmployeeAppCountUsageStat
            prominent
            clientEmployeeId={clientEmployeeId}
            clientId={clientId}
            dateRange={dateRange}
            applicationResourceId={filterValues.applicationResourceId}
            windowLabel={windowLabel}
          />
        </DashboardCell>
        <DashboardCell span={4}>
          <EmployeeOwnedAppsCountStat
            prominent
            clientEmployeeId={clientEmployeeId}
            clientId={clientId}
          />
        </DashboardCell>

        <DashboardCell span={12}>
          <DashboardWindowBar
            label={windowLabel}
            actions={
              <IconButton
                size="sm"
                icon="faFilter"
                onClick={() => setIsFilterPanelOpen(true)}
                outline="sm"
                showBubble={areFiltersActive}
                tooltip="Show filters"
              />
            }
          />
        </DashboardCell>

        </DashboardGrid>
          <div className={classes.activity}>
            <div
              className={classes.log}
              style={logHeight ? { height: logHeight } : undefined}
            >
              <HistoricalActivityTimelineCard
                clientId={clientId}
                clientEmployeeId={clientEmployeeId}
                dateRange={dateRange}
                applicationResourceId={filterValues.applicationResourceId}
                windowLabel={windowLabel}
              />
            </div>
            <div className={classes.detail}>
              <div className={classes.chart}>
                <DashboardWidget
                  title="Resource group usage by user"
                  definition={windowLabel}
                >
                  <DashboardChartFrame>
                    <ClientEmployeeAppUsageLineChart
                      employeeId={clientEmployeeId}
                      daysToShow={30}
                      startDate={dateRange[0] || undefined}
                      endDate={dateRange[1] || undefined}
                      parentCategoryId={filterValues.parentCategoryId}
                      applicationResourceId={
                        filterValues.applicationResourceId
                      }
                    />
                  </DashboardChartFrame>
                </DashboardWidget>
              </div>
              <div className={classes.usage}>
              <DashboardWidget
                title="Resource group usage"
                definition={windowLabel}
                actions={
                  <Box direction="row" gap={8} align="center">
                    <IconButton
                      size="sm"
                      icon="faTableColumns"
                      onClick={() => setIsColumnSettingsOpen(true)}
                      outline="sm"
                      tooltip="Column settings"
                    />
                    <IconButton
                      size="sm"
                      icon={isExporting ? "faArrowsSpin" : "faFileCsv"}
                      onClick={handleExportClick}
                      outline="sm"
                      tooltip={isExporting ? "Exporting..." : "Export CSV"}
                      disabled={isExporting}
                    />
                  </Box>
                }
              >
                <EmployeeResourceGroupsTable
                  clientId={clientId}
                  clientEmployeeId={clientEmployeeId}
                  pageSize={10}
                  showFooter={true}
                  showHeader={true}
                  showPageSizeDropdown={false}
                  dateRange={dateRange}
                  parentCategoryId={filterValues.parentCategoryId}
                  applicationResourceId={filterValues.applicationResourceId}
                />
              </DashboardWidget>
              </div>
            </div>
          </div>
      </div>

      <SlideInPanel
        isOpen={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        onCancel={handleFiltersReset}
        onApply={handleApplyFilters}
        cancelButtonText={areFiltersActive ? "Clear filters" : undefined}
        applyButtonText="Apply filters"
        title="Application Filters"
      >
        <EmployeeApplicationFilterForm
          ref={filterFormRef}
          clientEmployeeId={clientEmployeeId}
          onFilterChange={handleFilterChange}
          values={filterValues}
          errorsAPI={undefined}
        />
      </SlideInPanel>

      <EmployeeApplicationsTableColumnCustomization
        isOpen={isColumnSettingsOpen}
        onClose={() => setIsColumnSettingsOpen(false)}
      />
    </TableColumnSettingsProvider>
  );
};
